import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './PropertyCard.css';

export default function PropertyCard({ listing, index = 0 }) {
  const [imgError, setImgError] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, addToWatchlist, removeFromWatchlist, isInWatchlist } = useAuth();
  const { t } = useLanguage();
  const saved = isInWatchlist(listing.id);

  const photos = listing.images && listing.images.length > 0
    ? listing.images
    : listing.image ? [listing.image] : [];

  const prevPhoto = (e) => { e.stopPropagation(); setPhotoIdx(i => (i - 1 + photos.length) % photos.length); };
  const nextPhoto = (e) => { e.stopPropagation(); setPhotoIdx(i => (i + 1) % photos.length); };

  const handleSave = (e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    if (saved) {
      removeFromWatchlist(listing.id);
    } else {
      addToWatchlist(listing);
    }
  };

  const goToProperty = () => {
    navigate(`/property/${listing.id}`);
    window.scrollTo(0, 0);
  };

  const formatPrice = (price, type) => {
    if (type === 'rent') return '€' + price.toLocaleString() + t('card.perMonth');
    return '€' + price.toLocaleString();
  };

  const titleCase = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

  const currentPhoto = photos[photoIdx];
  const isSold = listing.status === 'sold';
  const isRent = listing.priceType === 'rent';
  const kickerLead = isRent ? t('card.forRent') : t('card.forSale');
  const kicker = listing.type ? `${kickerLead} · ${titleCase(listing.type)}` : kickerLead;
  const locationText = [listing.city, listing.region].filter(Boolean).join(', ');

  return (
    <div
      className={`prop-card animate-fade-up${isSold ? ' prop-card--sold' : ''}`}
      style={{ animationDelay: `${index * 0.1}s`, cursor: 'pointer' }}
      onClick={goToProperty}
    >

      <div className="prop-card__thumb" style={{ background: listing.gradient }}>
        {isSold && (
          <div className="prop-card__sold-ribbon">{t('card.soldBadge')}</div>
        )}
        {currentPhoto && !imgError ? (
          <img
            src={currentPhoto}
            alt={listing.title}
            className="prop-card__img"
            loading="lazy"
            decoding="async"
            width="640"
            height="448"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="prop-card__emoji">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.35}}>
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
        )}

        {photos.length > 1 && (
          <>
            <button className="prop-card__arrow prop-card__arrow--left" onClick={prevPhoto} aria-label={t('card.prevPhoto')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button className="prop-card__arrow prop-card__arrow--right" onClick={nextPhoto} aria-label={t('card.nextPhoto')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <div className="prop-card__photo-dots">
              {photos.map((_, i) => (
                <span key={i} className={`prop-card__dot ${i === photoIdx ? 'prop-card__dot--active' : ''}`} />
              ))}
            </div>
          </>
        )}

        {listing.is_partner && (
          <div className="prop-card__partner-ribbon">{t('card.partnerBadge')}</div>
        )}

        <button className={`prop-card__fav ${saved ? 'prop-card__fav--saved' : ''}`} onClick={handleSave} aria-label={t('card.savePropertyAria')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
      </div>

      <div className="prop-card__body">
        <div className="prop-card__kicker">{kicker}</div>
        <h3 className="prop-card__title" title={listing.title}>{listing.title}</h3>
        {locationText && <div className="prop-card__loc">{locationText}</div>}

        <div className="prop-card__specs">
          {listing.bedrooms != null && (
            <span className="prop-card__spec"><strong>{listing.bedrooms}</strong> {t('card.bedrooms')}</span>
          )}
          {listing.bedrooms != null && listing.bathrooms != null && (
            <span className="prop-card__spec-sep" aria-hidden="true">·</span>
          )}
          {listing.bathrooms != null && (
            <span className="prop-card__spec"><strong>{listing.bathrooms}</strong> {t('card.bathrooms')}</span>
          )}
          {(listing.bedrooms != null || listing.bathrooms != null) && listing.size != null && (
            <span className="prop-card__spec-sep" aria-hidden="true">·</span>
          )}
          {listing.size != null && (
            <span className="prop-card__spec"><strong>{listing.size}</strong> m²</span>
          )}
        </div>

        <div className="prop-card__footer">
          <div className="prop-card__price">{formatPrice(listing.price, listing.priceType)}</div>
          <div className="prop-card__view">{t('card.view')}</div>
        </div>
      </div>
    </div>
  );
}
